import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  getColorList,
  getPhoneTypes,
  getStandardViolations,
  getUser,
  getUserTenants,
  postUserLogin,
  getTenants,
  sendResetPassword,
} from "api";
import { ACTION_TYPE, AppContext } from "context/appContext";
import { ReactNode, useContext, useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import {
  ActionFunction,
  NavigateFunction,
  useFetcher,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { ROUTES, routeWithParams } from "router";
import {
  LoginActionHandler,
  LoginActionData,
  LoginActionResponse,
  PhoneType,
  Tenant,
  User,
  Violation,
  LoginResponseData,
} from "types";
import ReactGA from "react-ga4";
import AuthService from "utils/AuthService";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "context/themeContext";
import { deleteCookie, getCookieValue, setCookieValue } from "utils";
import AuturaTextField from "components/inputs/AuturaTextField";
import { ToastContext, TOAST_TYPE } from "context/toastContext";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { useFetcherKey } from "hooks/useFetcherKey";
import { getDevLogin } from "utils/devLogin";

const Login = (): ReactNode => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const theme = useTheme();
  const {fetcherKey} = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const { pathname } = useLocation();
  const displayToast = useContext(ToastContext);
  const { dispatch } = useContext(AppContext);
  const { setTenantBranding, logos } = useContext(ThemeContext);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [redirectURL, setRedirectURL] = useState<string>("");
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState<boolean>(false);
  // initialize the login page
  useEffect(() => {
    // Check for toast message from search parameters (e.g., from authentication errors)
    const toastMessage = searchParams.get("toastMessage");
    const toastType = searchParams.get("toastType");
    if (toastMessage) {
      // there is a render race condition, so I am adding to the end of the event loop
      setTimeout(() => {
        displayToast({
          type: (toastType as TOAST_TYPE) || TOAST_TYPE.INFO,
          message: decodeURIComponent(toastMessage),
        });
      }, 10);
      
      // Clean up URL parameters after showing the toast
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("toastMessage");
      newSearchParams.delete("toastType");
      const newUrl = window.location.pathname + (newSearchParams.toString() ? `?${newSearchParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // if last user is available, set the email
    const lastUser = getCookieValue("user");
    if (lastUser) {
      setRememberMe(true);
      setEmail(lastUser);
    }

    // local dev only: prefill the seed login from .env
    const devLogin = getDevLogin();
    if (devLogin) {
      setEmail(devLogin.email);
      setPassword(devLogin.password);
    }

    // check url params for redirected login
    const jwtToken = searchParams.get("jwt");

    if (jwtToken && jwtToken.length > 0) {
      const redirectURL = searchParams.get("redirectURL");
      if (redirectURL) {
        setRedirectURL(redirectURL);
      }
      AuthService.SetToken(jwtToken);
      handleAutoLogin(jwtToken);
      return;
    }

    // check cookies for restore session
    const token = AuthService.GetToken();
    const lastUrl = getCookieValue("lastUrl");

    if (token && lastUser && lastUrl) {
      // if we have a valid token and a username and a route, lets try to auto login
      setRedirectURL(lastUrl);
      const userToken = AuthService.DecodeJWT(token);
      const lastUserMatchesToken = userToken.email === lastUser;
      const tokenExpired = AuthService.IsTokenExpired(token);
      if (!tokenExpired && lastUserMatchesToken) {
        AuthService.SetToken(token);
        handleAutoLogin(token);
      }
    }
  }, []);

  // handle auto login
  const handleAutoLogin = async (jwt: string) => {
    setIsSubmitting(true);
    const userToken = AuthService.DecodeJWT(jwt);
    ReactGA.event({
      category: "Login",
      action: "Auto Login",
      label: userToken.email,
      value: 1,
    });
    fetcher.submit(
      {
        email: userToken.email,
        password: "",
        token: jwt,
        userID: userToken.id,
        handler: LoginActionHandler.AUTO,
      },
      {
        method: "POST",
        action: pathname,
        encType: "application/json",
      },
    );
  };

  // handle manual login
  const handleSubmit = async () => {
    setIsSubmitting(true);
    ReactGA.event({
      category: "Login",
      action: "Sign In Button Clicked",
      label: email,
      value: 1,
    });

    fetcher.submit(
      {
        email: email,
        password: password,
        userID: 0,
        handler: LoginActionHandler.MANUAL,
      },
      {
        method: "POST",
        action: pathname,
        encType: "application/json",
      },
    );
  };

  // set the initial state
  const setInitialState = (
    {
      user,
      userTenants,
      standardViolations,
      phoneTypes,
      colors,
    }: LoginResponseData,
    tenantToSelect: Tenant,
  ) => {
    // set the theme
    setTenantBranding({
      branding: tenantToSelect?.data?.branding,
      companyPath: tenantToSelect?.data?.companyPath,
    });
    // set the user
    dispatch({
      type: ACTION_TYPE.SET_USER,
      payload: user,
    });
    // set the tenants
    dispatch({
      type: ACTION_TYPE.SET_USER_TENANTS,
      payload: userTenants as Array<Tenant>,
    });
    // set the phone types
    dispatch({
      type: ACTION_TYPE.SET_PHONE_TYPES,
      payload: phoneTypes,
    });
    // set the standard violations
    dispatch({
      type: ACTION_TYPE.SET_STANDARD_VIOLATIONS,
      payload: standardViolations,
    });
    // set the colors
    dispatch({
      type: ACTION_TYPE.SET_COLORS,
      payload: colors,
    });
    // set active tenant
    dispatch({
      type: ACTION_TYPE.SET_ACTIVE_TENANT,
      payload: tenantToSelect,
    });
    // set the signature
    if (user.signature) { 
    dispatch({
        type: ACTION_TYPE.SET_SIGNATURE,
        payload: user.signature,
      });
    }
    ReactGA.set({
      tenant: tenantToSelect.name,
      tenant_group: tenantToSelect.tenantGroup?.name || "none",
      system: tenantToSelect.systemName,
      user_email: user.email,
      user_roles: user.roles
        .map((role) => {
          if (role?.tenantID === tenantToSelect.id) {
            return role.name;
          }
        })
        .join(","),
    });
  };

  const response = fetcher.data as LoginActionResponse | undefined;
  useEffect(() => {
    if (response) {
      if (response.status === 200) {
        handleSuccessResponse(response);
      } else {
        handleErrorResponse(response);
      }
    }
  }, [fetcher]);

  const handleErrorResponse = (res: LoginActionResponse): void => {
    if (res.handler === LoginActionHandler.RESET_PASSWORD) {
      displayToast({ type: TOAST_TYPE.ERROR, message: t("reset_password_error", { error: res.status, ns: "toasts" }) });
      setEmail("");
      setOpenResetPasswordDialog(false);
      return;
    }
    setIsSubmitting(false);
    const msg = t(`errors.login${res.status}`);
    displayToast({ type: TOAST_TYPE.ERROR, message: msg });
    AuthService.LogOut();
    dispatch({ type: ACTION_TYPE.LOGOUT });
  };

  const handleSuccessResponse = async (
    res: LoginActionResponse,
  ): Promise<void> => {

    if (res.handler === LoginActionHandler.RESET_PASSWORD) {
      displayToast({ type: TOAST_TYPE.SUCCESS, message: t("reset_password_sent", { email: res.email || "", ns: "toasts" }) });
      setEmail("");
      setOpenResetPasswordDialog(false);
      return;
    }


    if (
      !res.user ||
      !res.userTenants ||
      !res.standardViolations ||
      !res.phoneTypes ||
      !res.colors ||
      res.userTenants.length === 0
    ) {
      displayToast({ type: TOAST_TYPE.ERROR, message: t("errors.login404") });
      return;
    }

    // save the email to cookies
    if (rememberMe) {
      setCookieValue("user", email);
    } else {
      deleteCookie("user");
    }
    const tenant = getTenantFromURL(redirectURL, res.userTenants);

    setInitialState(
      {
        user: res.user,
        userTenants: res.userTenants,
        standardViolations: res.standardViolations,
        phoneTypes: res.phoneTypes,
        colors: res.colors,
      },
      tenant,
    );

    // if redirect url is provided, navigate to that url
    if (redirectURL && URL.canParse(redirectURL)) {
      const url = new URL(redirectURL);
      navigate(url.pathname);
      return;
    }

    navigate(
      routeWithParams(ROUTES.JOBS, [tenant?.id || res.userTenants[0].id]),
    );
  };

  const handleOpenResetPasswordDialog = () => {
    setOpenResetPasswordDialog(true);
  };

  const handleCloseResetPasswordDialog = () => {
    setOpenResetPasswordDialog(false);
  };

  const handleResetPassword = async () => {
    fetcher.submit(
      {
        email: email,
        handler: LoginActionHandler.RESET_PASSWORD,
      },
      {
        method: "POST",
        action: pathname,
        encType: "application/json",
      },
    );
  };

  return (
    <>
    {openResetPasswordDialog && (
      <ResetPasswordDialog
        open={openResetPasswordDialog}
        onCancel={handleCloseResetPasswordDialog}
        onSubmit={handleResetPassword}
        email={email}
        setEmail={setEmail}
        fetcherKey={fetcherKey}
      />
    )}
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <Card
        raised
        sx={{
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          height: {
            xs: "100dvh",
            sm: `calc(100dvh - 48px)`,
          },
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"row"}
          maxHeight={`calc(100dvh - 448px)`}
          sx={{
            objectFit: "contain",
          }}
          width={"100%"}
        >
          <img
            src={logos.logoUrl}
            alt="logo"
            style={{
              width: "100%",
              maxHeight: `calc(100dvh - 448px)`,
              maxWidth: `calc(100dvh - 448px)`,
              padding: `${logos.logoPadding}px`,
            }}
          />
        </Box>
        <CardContent
          sx={{
            px: {
              xs: 2,
              sm: 12,
            },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            height: 416,
          }}
        >
          <Stack
            direction={"column"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={1}
            sx={{ width: "100%", height: 349 }}
          >
            <Typography variant="h4">{t("signin")}</Typography>
            <Box width={"100%"}>
              <AuturaTextField
                label={t("email")}
                fullWidth
                tabIndex={1}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                name="email"
                value={email}
                validators={["required", "isEmail"]}
                errorMessages={["this field is required", "email is not valid"]}
              />
              <FormControlLabel
                sx={{ pl: 2, mt: -0.5 }}
                control={
                  <Checkbox
                    checked={rememberMe}
                    size={"small"}
                    tabIndex={-1}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">{t("rememberMe")}</Typography>
                }
              />
            </Box>
            <Box width={"100%"}>
              <AuturaTextField
                type={showPassword ? "text" : "password"}
                value={password}
                tabIndex={2}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("password")}
              />
              <Button onClick={handleOpenResetPasswordDialog} variant="text">
              <Typography
                variant="caption"
                color={theme.palette.info.main}
                sx={{ pl: 2 }}
              >
                  {t("forgotPassword") + "?"}
                </Typography>
              </Button>

            </Box>
            <Box py={3} width={"100%"} height={98}>
              {isSubmitting ? (
                <LinearProgress
                  style={{ height: 50, width: "100%" }}
                  className="card"
                  color="primary"
                />
              ) : (
                <Button
                  centerRipple
                  fullWidth
                  color={"primary"}
                  type="submit"
                  variant="contained"
                  sx={{ height: 50 }}
                  disabled={!email || !password}
                >
                  {t("signin")}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </ValidatorForm>
    </>
  );
};

export const loginAction: ActionFunction = async ({
  request,
}): Promise<LoginActionResponse> => {
  const { email, password, userID, handler } =
    (await request.json()) as LoginActionData;

  let user: User | null = null;
  const colors: Array<string> = [];
  const phoneTypes: Array<PhoneType> = [];
  const standardViolations: Array<Violation> = [];
  const userTenants: Array<Tenant> = [];

  if (handler === LoginActionHandler.AUTO) {
    try {
      user = await getUser(userID);
      const decoded = AuthService.DecodeJWT(user.jwtToken || "");
      if (decoded.id > 0 && decoded.id === user?.id) {
        AuthService.SetToken(user.jwtToken || "");
      }
    } catch (e) {
      return {
        handler,
        status: 404,
        colors,
        phoneTypes,
        standardViolations,
        userTenants,
        user,
      };
    }

    try {
      const responses = await Promise.all([
        AuthService.IsSuperAdmin()
          ? getTenants(false)
          : getUserTenants({ userID: user.id, includeDisabled: false }),
        getStandardViolations(),
        getPhoneTypes(),
        getColorList(),
      ]);

      if (responses.every((res) => res)) {
        userTenants.push(...responses[0]);
        standardViolations.push(...responses[1]);
        phoneTypes.push(...responses[2]);
        colors.push(...responses[3]);
      }
    } catch (e) {
      return {
        handler,
        status: 500,
        colors,
        phoneTypes,
        standardViolations,
        userTenants,
        user,
      };
    }
  }

  if (handler === LoginActionHandler.MANUAL) {
    try {
      const res = await postUserLogin({ email, password, clientOS: "web" });
      if (!res.ok) {
        return {
          handler,
          status: res.status,
          colors,
          phoneTypes,
          standardViolations,
          userTenants,
          user,
        };
      }
      const response: LoginResponseData = await res.json();
      const decoded = AuthService.DecodeJWT(response.user.jwtToken || "");
      if (decoded.id > 0 && decoded.id === response.user?.id) {
        AuthService.SetToken(response.user.jwtToken || "");
      }
      user = response.user;
      userTenants.push(...response.userTenants);
      standardViolations.push(...response.standardViolations);
      phoneTypes.push(...response.phoneTypes);
      colors.push(...response.colors);
    } catch (e) {
      return {
        handler,
        status: 500,
        colors,
        phoneTypes,
        standardViolations,
        userTenants,
        user,
      };
    }
  }

  if (handler === LoginActionHandler.RESET_PASSWORD) {
    try {
      const res = await sendResetPassword(email);
      if (!res.ok) {
        return {
          handler,
          status: res.status,
          colors, 
          phoneTypes,
          standardViolations,
          userTenants,
          user,
        };
      }
      return {
        handler,
        status: res.status,
        colors,
        phoneTypes,
        standardViolations,
        userTenants,
        user,
        email: email,
      };
    } catch (e) {
      return {
        handler,
        status: 500,
        colors,
        phoneTypes,
        standardViolations,
        userTenants,
        user,
      };
    }
  }

  return {
    handler,
    status: 200,
    colors,
    phoneTypes,
    standardViolations,
    userTenants,
    user,
  };
};

const getTenantFromURL = (url: string, tenants: Tenant[]): Tenant => {
  let pathComponents = url.split("/");

  try {
    const urlParser = new URL(url);
    pathComponents = urlParser.pathname.split("/");
  } catch (e) {
    return tenants[0];
  }

  if (pathComponents.length > 2 && pathComponents[2]) {
    const foundTenant = tenants.find((t: Tenant) => {
      return pathComponents[2] === t.id.toString();
    });
    if (foundTenant) {
      return foundTenant;
    }
  }

  return tenants[0];
};

export default Login;
